/**
 * Reset Password Page — landing page for the token link from the reset email
 */

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button, Input } from '../../components/ui';
import { api } from '../../lib/api';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    if (password !== confirm) {
      setError('Die Passwörter stimmen nicht überein');
      return;
    }
    setError(undefined);
    setIsLoading(true);
    try {
      await api.post('/auth/reset-password', { token, newPassword: password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch {
      setError('Der Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen an.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 py-12 px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Ungültiger Link
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Diesem Link fehlt das Reset-Token. Bitte fordern Sie einen neuen Link an.
          </p>
          <Link
            to="/forgot-password"
            className="text-sm text-primary-600 hover:text-primary-500 font-medium"
          >
            Neuen Link anfordern
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">FleetTracko Admin</h1>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
          {done ? (
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Passwort geändert
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Sie können sich jetzt mit Ihrem neuen Passwort anmelden. Sie werden weitergeleitet…
              </p>
              <Link
                to="/login"
                className="text-sm text-primary-600 hover:text-primary-500 font-medium"
              >
                Zur Anmeldung
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Neues Passwort festlegen
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  label="Neues Passwort"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  disabled={isLoading}
                />
                <Input
                  label="Passwort bestätigen"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  error={error}
                  autoComplete="new-password"
                  disabled={isLoading}
                />
                <Button type="submit" className="w-full" loading={isLoading}>
                  Passwort speichern
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
